import { Theme, alpha } from "@mui/material/styles";
import { inputAdornmentClasses } from "@mui/material/InputAdornment";

// Common typography styles
export const commonTypography = {
  fontStyle: "normal" as const,
  fontWeight: 500,
  fontSize: "13px",
};

// Common field styles - theme-aware
export const commonFieldStyle = (theme: Theme) => ({
  ...commonTypography,
  borderRadius: "10px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.23)"
        : "rgba(0, 0, 0, 0.23)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.4)"
        : "rgba(0, 0, 0, 0.4)",
  },
});

// Common field input style
export const commonFieldInputStyle = {
  "& .MuiInputBase-input": {
    ...commonTypography,
  },
};

// Search field placeholder style - theme-aware
export const searchFieldPlaceholderStyle = (theme: Theme) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(0, 0, 0, 0.02)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.08)"
          : "rgba(0, 0, 0, 0.04)",
    },
    "&.Mui-focused": {
      backgroundColor: "transparent",
    },
  },
  "& .MuiInputBase-input": {
    fontSize: "0.875rem",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  "& .MuiInputBase-input::placeholder": {
    textAlign: "left" as const,
    fontSize: "0.875rem",
    opacity: 0.6,
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.875rem",
    maxWidth: "calc(100% - 44px)",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  [`& .${inputAdornmentClasses.root}`]: {
    marginLeft: "4px",
    flexShrink: 0,
  },
});

// Grid breakpoints for filter fields - optimized to fit all controls in one row
export const filterFieldGridProps = {
  xs: 12,
  sm: 6,
  md: 2.4,
  lg: true, // flex-grow to fill available space
};

export const searchFieldGridProps = {
  xs: 12,
  sm: 12,
  md: 2.4,
  lg: true, // flex-grow to fill available space
};

export const templateSelectorGridProps = (hasActiveTemplate: boolean) => ({
  xs: 12,
  sm: 6,
  md: hasActiveTemplate ? 3.6 : 2.4,
  lg: true,
});

// Base icon button styles
export const iconButtonBase = (isActive: boolean) => ({
  color: isActive ? "primary.main" : "text.secondary",
  transition: "all 0.2s ease",
  borderRadius: "10px",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.06)",
    transform: "translateY(-1px)",
  },
});

// Action icon button - enhanced styling with consistent size
export const actionIconButton = (theme: Theme, isActive: boolean = false) => ({
  width: 34,
  height: 34,
  minWidth: 34,
  borderRadius: "8px",
  color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
  backgroundColor: isActive
    ? alpha(theme.palette.primary.main, 0.08)
    : "transparent",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: isActive
      ? alpha(theme.palette.primary.main, 0.15)
      : alpha(theme.palette.action.hover, 0.08),
    transform: "translateY(-1px)",
  },
});

// Primary action button (for create new, etc.)
export const primaryActionButton = (theme: Theme) => ({
  width: 34,
  height: 34,
  minWidth: 34,
  borderRadius: "8px",
  color: theme.palette.primary.main,
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.15),
    transform: "translateY(-1px)",
  },
});

// Button group container - no border, just layout
export const buttonGroupContainer = (theme: Theme) => ({
  display: "flex",
  alignItems: "center",
  gap: 0.5,
});

// Vertical divider for button groups
export const buttonDivider = (theme: Theme) => ({
  width: "1px",
  height: "24px",
  backgroundColor: alpha(theme.palette.divider, 0.5),
  mx: 0.5,
});

// Menu item styles - theme-aware
export const menuItemStyle = (theme: Theme) => ({
  ...commonTypography,
  height: "40px",
  margin: "2px 4px",
  borderRadius: "8px",
  transition: "all 0.15s ease",
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(0, 0, 0, 0.04)",
  },
  "&.Mui-selected": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.12)"
        : alpha(theme.palette.primary.main, 0.08),
    "&:hover": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.16)"
          : alpha(theme.palette.primary.main, 0.12),
    },
  },
});

// Select all menu item style - theme-aware
export const selectAllMenuItemStyle = (theme: Theme) => ({
  justifyContent: "left" as const,
  borderRadius: "8px",
  margin: "4px",
  borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.08)"}`,
  paddingBottom: "8px",
  marginBottom: "4px",
});

export const selectAllCheckboxStyle = {
  padding: "0 8px",
};

export const selectAllLabelStyle = {
  userSelect: "none" as const,
  margin: 0,
  "& .MuiFormControlLabel-label": {
    fontSize: "0.875rem",
    fontWeight: 600,
  },
};

// Prevent multi-select tooltips from flipping over open dropdown menus
export const multiSelectTooltipPopperProps = {
  modifiers: [
    {
      name: "flip",
      enabled: false,
    },
    {
      name: "preventOverflow",
      options: {
        altAxis: true,
        padding: 8,
      },
    },
  ],
};

// Dropdown menu props - theme-aware
export const dropdownMenuProps = (theme: Theme) => ({
  PaperProps: {
    sx: {
      maxHeight: 280,
      overflowY: "auto",
      overflowX: "hidden",
      borderRadius: "12px",
      boxShadow:
        theme.palette.mode === "dark"
          ? "0 8px 32px rgba(0,0,0,0.4)"
          : "0 8px 32px rgba(0,0,0,0.12)",
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      marginTop: "4px",
      "&::-webkit-scrollbar": {
        width: "6px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "transparent",
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.2)"
            : "rgba(0, 0, 0, 0.15)",
        borderRadius: "3px",
        "&:hover": {
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.35)"
              : "rgba(0, 0, 0, 0.25)",
        },
      },
      "& .MuiList-root": {
        padding: "6px",
      },
    },
  },
});

// Form control styling
export const formControlStyle = (theme: Theme) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    transition: "all 0.2s ease",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: alpha(theme.palette.primary.main, 0.4),
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderWidth: "1.5px",
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.875rem",
  },
});

// Drawer styles
export const drawerHeaderStyle = (theme: Theme) => ({
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  p: 2.5,
  position: "sticky" as const,
  top: 0,
  zIndex: 1,
});

export const drawerSectionStyle = (theme: Theme) => ({
  px: 2.5,
  py: 1.5,
});

export const drawerLabelStyle = (theme: Theme) => ({
  fontSize: "0.75rem",

  fontWeight: 600,
  color: alpha(theme.palette.text.secondary, 0.8),
  textTransform: "capitalize" as const,
  letterSpacing: "0.5px",
  mb: 1,
});

// Zoom control container - with border
export const zoomControlContainer = (theme: Theme) => ({
  display: "flex",
  alignItems: "center",
  gap: 0.25,
  px: 0.5,
  py: 0.25,
  borderRadius: "10px",
  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
});

// Alert styling
export const infoAlertStyle = (theme: Theme) => ({
  mb: 2,
  borderRadius: "12px",
  backgroundColor: alpha(theme.palette.info.main, 0.08),
  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
  "& .MuiAlert-message": {
    fontSize: "0.875rem",
  },
  "& .MuiAlert-icon": {
    color: theme.palette.info.main,
  },
});
